// kernel/useScoringCampaigns.ts
import { sanitizeUserFacingError } from '#lib/failed-generation';
import { agentIsWorking } from '#lib/chat/agentIsWorking';
import { type CampaignTargetKind, type SourcingPresetParams } from '#types/types';
import { isEntityFocusSubject } from '#lib/leasing/session-focus';
import { commitToMandate, createMandate, MANDATE_DEFAULT_TARGET_SCORE } from '#lib/leasing/sourcingMandate';
import { creditsForBrands } from '#lib/leasing/sourceBudget';
import { resolveDefaultSourcePortfolioId } from '#lib/source-defaults';
import { campaignMirrorPatch, isCampaignTerminal } from '#lib/leasing/campaignCard';
import { normalizePortfolioFitCountryCodes } from '#lib/leasing/portfolio-fit-country-code-utils';
import { useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  type KernelHandle,
  type useScoringCampaignsReturnType,
  type RealtimeEvent,
  type ScoringCriterion,
  type ScriptInstance,
  type SourcingCampaignDTO,
  type LaunchSourcingCampaignOptions,
  type InventoryMutationOperation,
  type PortfolioFitCampaignDTO,
  type PropertySourcingCategoryFilters,
  type ReapplySourcingRubricResult,
  type SourceCampaignBrief,
  type SourceBudgetProposal,
} from '#types/types';
import { toast } from 'sonner';
import { readCampaignRow, resolveCampaignPropertyId } from '#lib/leasing/campaignRow';
import { markEnriching } from '#lib/leasing/enrichingDims';

interface useScoringCampaignsArgs {
  kernel: React.RefObject<KernelHandle | null>;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * The route emits every re-scored Lead over the socket BEFORE it answers, and the client still
 * has to apply and paint that last chunk. So the shortlist's "being reworked" state outlives the
 * request by a short tail; without it the sweep dies a frame before the final rows settle.
 */
const RESCORE_SETTLE_TAIL_MS = 900;
// The Launch bar's first pass, per asset: enough brands to learn what scores (a quarter of a small
// ceiling once bought ONE brand, 2026-09-28); the server driver widens from there.
const FIRST_PASS_BRANDS = 20;
// Kept at the value PortfolioFitCampaignRenderer used, so moving its stop into the kernel refetches
// exactly the same window of fits it always did.
const PORTFOLIO_FIT_STOP_PREFETCH_LIMIT = 120;

/**
 * Ask the server which conversation belongs to a campaign. The server is the only place that can
 * answer: it sees every session and every campaign of the asset at once, decides in one
 * transaction, and creates and binds one if the campaign has none. Returns '' if it could not
 * answer, which callers must treat as "leave the user where they are", never as "none".
 */
const fetchCampaignConversation = async (
  campaignId: string,
): Promise<{ sessionId: string; notFound: boolean }> => {
  try {
    const response = await fetch(
      `/api/inventory/sourcing-campaigns/${encodeURIComponent(campaignId)}/conversation`,
      { method: 'POST', cache: 'no-store' },
    );
    // 404 means "this id is not a sourcing campaign", which is the ordinary case for the
    // portfolio-fit and unit-fit cards that share the same open handler. It is an answer, not a
    // failure, and must not raise anything in front of the user.
    if (response.status === 404) return { sessionId: '', notFound: true };
    if (!response.ok) throw new Error(String(response.status));
    const payload = (await response.json()) as { sessionId?: unknown };
    return {
      sessionId: typeof payload.sessionId === 'string' ? payload.sessionId : '',
      notFound: false,
    };
  } catch (error) {
    console.error('[useScoringCampaigns] could not resolve the campaign conversation', error);
    return { sessionId: '', notFound: false };
  }
};

/**
 * Raises the shared shortlist re-score flag and returns its release. Kept at the kernel level on
 * purpose: every caller (the right-bar panel, the agent's action registry, a campaign renderer)
 * lights the same state without having to know about it.
 */
const markRescoreInFlight = (kernel: React.RefObject<KernelHandle | null>): (() => void) => {
  const k = kernel.current;
  if (!k) return () => undefined;
  k.setState((state: any) => ({ sourceRescoreInFlight: (state.sourceRescoreInFlight ?? 0) + 1 }));
  let released = false;
  return () => {
    if (released) return;
    released = true;
    setTimeout(() => {
      kernel.current?.setState((state: any) => ({
        sourceRescoreInFlight: Math.max(0, (state.sourceRescoreInFlight ?? 0) - 1),
      }));
    }, RESCORE_SETTLE_TAIL_MS);
  };
};

/**
 * A launch is the strongest recency signal for the asset picker's "Récents" group. Recency is
 * personal but not per-browser: it lives in User.preferences, so the same leasing manager gets
 * their centres on any machine.
 */
const rememberRecentAsset = (k: KernelHandle | null, assetId: string): void => {
  const trimmed = typeof assetId === 'string' ? assetId.trim() : '';
  if (!trimmed || !k) return;
  const previous = k.getUserPreferences().sourceRecentAssetIds;
  const known = Array.isArray(previous) ? previous : [];
  // Already the most recent: nothing moved, so don't spend a write on it.
  if (known[0] === trimmed) return;
  void k.updateAndSaveUserPreference(
    'sourceRecentAssetIds',
    [trimmed, ...known.filter((id) => id !== trimmed)].slice(0, 5),
  );
};

/**
 * ONE BRIEF, WRITTEN ONTO EVERY SELECTED ASSET, before the run reads it back off them.
 *
 * The launch route reads exclusions and category/surface filters from the Property entity,
 * so the brief is persisted first (mirrors the portfolio branch; before this, the panel's asset
 * filters were display-only and never applied). The form has a single set of filters (surface,
 * sectors, positioning), so a multi-asset launch has a single brief, and the launch route builds
 * each asset's candidate pool from the asset's own stored filters. Persisting the brief onto all of
 * them is what makes those pools agree with it.
 *
 * It also OVERWRITES each selected asset's previously stored filters, which is what the per-asset
 * fan-out did before and what a later single-asset launch on one of them will then read. The two
 * exclusion toggles are form-level too, and are written onto every selected asset the same way.
 * Nothing about scoring is written: every run is scored on the one grid (briefCriteria.ts).
 *
 * Exported for kernel/sourceCampaignBrief.test.ts: these two "only write it when..." rules are the
 * whole reason a brief does not wipe an asset's real configuration.
 */
export const buildAssetBriefUpdates = (brief: SourceCampaignBrief): InventoryMutationOperation[] => {
  const categoryFilters: PropertySourcingCategoryFilters = {
    ...(brief.minimumFitScore > 0 ? { portfolioFitMinimumScore: Math.round(brief.minimumFitScore) } : {}),
    ...(brief.cnccActivities.length > 0
      ? { acceptedCnccActivities: brief.cnccActivities }
      : {}),
    ...(brief.surfaceMinSqm > 0 ? { surfaceMinSqm: Math.round(brief.surfaceMinSqm) } : {}),
    ...(brief.surfaceMaxSqm > 0 ? { surfaceMaxSqm: Math.round(brief.surfaceMaxSqm) } : {}),
    ...(brief.formats?.length ? { acceptedFormats: [...brief.formats] } : {}),
  };

  // THE TWO CONTROLS THAT REACHED NOTHING. Both were read back from the portfolio's portfolio-fit
  // columns, which asset sourcing stopped consulting when that path was retired, so the slider and
  // the toggle were decoration: a run asking for five stores minimum analysed a one-restaurant
  // brand and scored it 75 (2026-08-18). They ride with the rest of the brief now, even when either
  // is the only non-default filter in the draft.
  if (brief.minimumNetworkSize > 1) categoryFilters.minimumNetworkSize = Math.round(brief.minimumNetworkSize);
  if (brief.requireKnownWebsite) categoryFilters.requireKnownWebsite = true;
  // On by default: only the user's choice to keep off-scope brands needs writing.
  if (brief.excludeOffScope === false) categoryFilters.excludeOffScope = false;
  // The countries were kept on the PORTFOLIO, in a column of the retired macro pass, so "only
  // France" was set on a run and gone from the form the next time it opened.
  if (brief.countryCodes.length > 0) categoryFilters.countryCodes = [...brief.countryCodes];

  return brief.assetIds.map((assetId) => ({
    action: 'UPDATE' as const,
    entity: 'Property' as const,
    id: assetId,
    payload: {
      sourcingExclusionTenants: brief.excludeOnsiteTenants,
      sourcingExclusionRecentLeads: brief.excludeRecentlyScored,
      // Only write filters we actually have. Clearing them requires a non-empty draft
      // (the user emptied a field); an EMPTY draft means the panel never managed to
      // seed itself, and persisting it would silently wipe the asset's real surface
      // range and CNCC sectors, which is how a 1000-4000 m² brief became "no range"
      // and the surface criterion vanished from the run.
      ...(Object.keys(categoryFilters).length > 0
        ? { sourcingCategoryFilters: categoryFilters }
        : {}),
    },
  }));
};

const buildOptimisticStopMetrics = (metrics: unknown, stoppedAtIso: string): unknown => {
  const base = isRecord(metrics) ? metrics : {};
  const baseWorker = isRecord(base.worker) ? base.worker : {};

  return {
    ...base,
    worker: {
      ...baseWorker,
      stopReason: 'USER_REQUEST',
      userStoppedAt: stoppedAtIso,
    },
  };
};

export const useScoringCampaigns = ({ kernel }: useScoringCampaignsArgs): useScoringCampaignsReturnType => {
  const sourceBudgetProposal = useCallback<useScoringCampaignsReturnType['sourceBudgetProposal']>(async (campaignId, request) => {
    const k = kernel.current;
    if (!k) throw new Error('Conversation unavailable.');
    const id = request.proposalId;
    if (id && k.getState().sourceBudgetProposals[id]?.busy) throw new Error('This credit proposal is already being processed.');
    if (id) k.setState(state => ({ sourceBudgetProposals: { ...state.sourceBudgetProposals, [id]: { ...state.sourceBudgetProposals[id], busy: true, error: undefined } } }));
    try {
      const response = await fetch(`/api/inventory/sourcing-campaigns/${encodeURIComponent(campaignId)}/budget-proposal`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(request),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'The credit proposal could not be updated.');
      // 'latest' answers null when the conversation holds no proposal.
      if (!result) return null;
      const proposal = result as SourceBudgetProposal;
      k.setState(state => ({ sourceBudgetProposals: { ...state.sourceBudgetProposals, [proposal.id]: { proposal, busy: false } } }));
      if (request.action === 'accept' && proposal.status === 'accepted') {
        k.logUserAction('Source credit increase approved', { campaignId, proposalId: proposal.id, creditCeiling: proposal.creditCeiling });
        await k.syncEntity('Campaign', campaignId, { force: true });
      }
      return proposal;
    } catch (error) {
      const message = sanitizeUserFacingError(error instanceof Error ? error.message : String(error));
      if (id) k.setState(state => ({ sourceBudgetProposals: { ...state.sourceBudgetProposals, [id]: { ...state.sourceBudgetProposals[id], busy: false, error: message } } }));
      // A background look for a posted proposal is not an action the user took: it fails quietly.
      if (request.action !== 'latest') kernel.current?.handleError(message);
      throw error;
    }
  }, [kernel]);
  const LEAD_PREFETCH_LIMIT = 120;
  const PORTFOLIO_FIT_PREFETCH_LIMIT = 120;
  const processedCampaignCompletionsRef = useRef<Set<string>>(new Set());
  /**
   * Which campaign-open is the current one. Opening resolves a conversation over the network
   * before it touches the page, so a second click (or a close) during that round trip must be
   * able to say "you are stale" to the first. Compared by value, not by campaign id: opening the
   * same campaign twice in a row still means only the later attempt owns the screen.
   */
  const openCampaignRequestRef = useRef(0);
  const makeCampaignCompletionKey = useCallback(
    (entityType: 'SourcingCampaign' | 'PortfolioFitCampaign', campaignId: string) =>
      `${entityType}:${campaignId}`,
    [],
  );
  const readKernelDraftCampaignBudget = useCallback((propertyId: string): number | undefined => {
    const k = kernel.current;
    if (!k) return undefined;
    const draftState = k.readEntityKernelState('Property', propertyId);
    const rawBudget = draftState?.sourcingBudgetCreditsDraft;
    if (typeof rawBudget !== 'number' || !Number.isFinite(rawBudget) || rawBudget <= 0) {
      return undefined;
    }
    return Math.max(1, Math.floor(rawBudget));
  }, [kernel]);

  const reportCampaignCompletion = useCallback(async (
    campaignId: string,
    propertyId?: string
  ) => {
    const k = kernel.current;
    if (!k) return;
    const completionKey = makeCampaignCompletionKey('SourcingCampaign', campaignId);
    if (processedCampaignCompletionsRef.current.has(completionKey)) return;

    k.playUISound('SUCCESS');
    processedCampaignCompletionsRef.current.add(completionKey);

    await Promise.all([
      k.queryInventory('Lead', {
        filters: {
          ...(propertyId ? { propertyId } : {}),
          sortBy: 'fitScore',
          sortDir: 'desc',
        },
        limit: LEAD_PREFETCH_LIMIT,
        offset: 0,
        force: true,
        ttl: 0,
      }),
      k.syncEntity('Campaign', campaignId),
    ]);
    toast.success('Sourcing campaign completed! New candidates available.');
  }, [kernel, makeCampaignCompletionKey]);


  const handleCampaignEvent = useCallback((event: RealtimeEvent) => {
    const k = kernel.current;
    if (!k) return;

    if (event.eventType !== 'UPDATE') {
      return;
    }

    // Fan the legacy event out to the unified `Campaign` row before the per-family branches below.
    // The dashboard reads inventory.Campaign.registry, which nothing emits under, so without this
    // a launched run sat on RUNNING with a stale count until the user reloaded. One place here
    // rather than a new emit at each of the writers, because the mirror trigger already gives the
    // two rows the same id.
    //
    // Patches an EXISTING row only: a legacy payload carries no targetKind and no propertyIds, so
    // it can never be enough to mint a valid CampaignDTO, and a half-built row would render worse
    // than a stale one.
    const mirrored = campaignMirrorPatch(event.payload);
    if (Object.keys(mirrored).length > 0) {
      k.setInventorySlice('Campaign', (prev) => {
        const current = prev.registry[event.entityId];
        if (!current) return prev;
        // NO-OP WHEN NOTHING MOVED, and this is a performance guard, not a tidiness one.
        // CampaignDashboard subscribes to the WHOLE Campaign registry, so any new registry object
        // re-renders the dashboard and every card on it. A running campaign emits progress events
        // continuously (throttled to ~300ms upstream), and most carry the same status it already
        // had, so writing unconditionally re-rendered the entire dashboard several times a second
        // for the whole length of a run. Returning `prev` unchanged makes Zustand skip it.
        if (Object.entries(mirrored).every(([key, value]) => {
          const before = (current as unknown as Record<string, unknown>)[key];
          return key === 'metrics' ? Object.is(before, value) : before === value;
        })) {
          return prev;
        }
        return {
          ...prev,
          lastActivity: Date.now(),
          registry: { ...prev.registry, [event.entityId]: { ...current, ...mirrored } },
        };
      });
      // Counts live in three other tables and are resolved server-side, so a status alone cannot
      // refresh them. Once the run stops moving, ask the detail route for the authoritative row.
      if (isCampaignTerminal(mirrored.status)) {
        void k.syncEntity('Campaign', event.entityId);
      }
    }

    if (event.entityType === 'SourcingCampaign') {
      const payload = event.payload as Partial<SourcingCampaignDTO>;
      const campaignId = event.entityId;
      if (payload.status === 'COMPLETE') {
        const existing = readCampaignRow(k.getState().inventory, campaignId);
        const propertyId =
          typeof payload.propertyId === 'string' && payload.propertyId.length > 0
            ? payload.propertyId
            // The unified row carries its coverage as an array, so the primary asset comes off the
            // shared reader rather than a scalar that only the legacy payload had.
            : resolveCampaignPropertyId(existing) || undefined;
        void reportCampaignCompletion(campaignId, propertyId);
      }
      return;
    }

  }, [kernel, reportCampaignCompletion]);

  /**
   * LAUNCH AN ASSET SOURCING RUN, the thing the right bar's Launch button does at asset scope.
   *
   * ONE CAMPAIGN, HOWEVER MANY ASSETS: `property` takes the whole selection. A list is the
   * multi-asset shape the Launch button sends, a scalar the single-asset shape the agent's
   * commands send, and each is forwarded to the route in the shape it arrived in - the EXTEND
   * path in particular is documented against a body carrying a single scalar `propertyId`
   * (app/api/inventory/sourcing-campaigns/route.ts).
   */
  const launchSourcingCampaign = useCallback(async (
    property: string | string[],
    options?: LaunchSourcingCampaignOptions,
  ) => {
    const k = kernel.current;
    if (!k) throw new Error('Kernel not available');
    const primaryRequestedPropertyId = (Array.isArray(property) ? property[0] : property) ?? '';

    try {
      let effectivePayload = options?.payload ? { ...options.payload } : undefined;
      const rawExplicitBudget = effectivePayload?.maxBudgetCredits;
      const normalizedExplicitBudget =
        typeof rawExplicitBudget === 'number' && Number.isFinite(rawExplicitBudget) && rawExplicitBudget > 0
          ? Math.max(1, Math.floor(rawExplicitBudget))
          : undefined;

      if (effectivePayload) {
        if (normalizedExplicitBudget !== undefined) {
          effectivePayload.maxBudgetCredits = normalizedExplicitBudget;
        } else if (effectivePayload.maxBudgetCredits !== undefined) {
          delete effectivePayload.maxBudgetCredits;
        }
      }

      if (normalizedExplicitBudget === undefined) {
        const kernelBudgetCredits = readKernelDraftCampaignBudget(primaryRequestedPropertyId);
        if (kernelBudgetCredits !== undefined) {
          effectivePayload = {
            ...(effectivePayload ?? {}),
            maxBudgetCredits: kernelBudgetCredits,
          };
        }
      }

      const hasPayload = Boolean(effectivePayload && Object.keys(effectivePayload).length > 0);
      const response = await fetch('/api/inventory/sourcing-campaigns', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(Array.isArray(property) ? { propertyIds: property } : { propertyId: property }),
          ...(hasPayload ? { payload: effectivePayload } : {}),
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to launch sourcing campaign');
      }

      const campaign = await response.json() as SourcingCampaignDTO;
      await Promise.all([
        k.syncEntity('Campaign', campaign.id),
        k.queryInventory('Lead', {
          filters: { lastCampaignId: campaign.id, sortBy: 'fitScore', sortDir: 'desc' },
          limit: LEAD_PREFETCH_LIMIT,
          offset: 0,
          force: true,
          ttl: 0,
        }),
      ]);
      // THE PRIMARY ASSET, read off the campaign the server actually created and never guessed
      // from the selection. The two differ: an asset whose filters match nothing is dropped from
      // the run, so the first asset PICKED may not be the first asset COVERED. Stamping the
      // conversation's subject with a dropped asset bound the chat to a shortlist that does not
      // include it, and the selection guard then closed the campaign the instant it opened.
      const subjectPropertyId =
        (typeof campaign.propertyId === 'string' && campaign.propertyId.trim()) ||
        primaryRequestedPropertyId;
      // Keep the agent in the Source Center: open the launched campaign in the unified sourcing
      // view rather than bouncing to the Property entity page. Focusing the Property with
      // ORCHESTRATE_SOURCING makes ChatContextDashboard render SourcingCenterView; the
      // selected-campaign state opens the campaign, which describes its own reach.
      k.setState({
        sourceSelectedCampaignId: campaign.id,
        sourceCreatingCampaign: false,
      });

      // STAY IN THIS CONVERSATION. `focusEntityUI` used to run here, and for a scoped-home entity
      // it resolves the ASSET'S HOME session, a different one from the draft the user has been
      // talking in. So launching wiped the screen: the rubric discussion, the validation, the
      // whole setup exchange replaced by an empty chat with one line in it. The draft session
      // already carries this exact subject (beginSourcingCampaignDraft puts it there), so the
      // subject only needs re-asserting on the CURRENT session, which is also what makes
      // ChatContextDashboard render SourcingCenterView. Draft, launch and shortlist are one
      // continuous thread.
      const activeSessionId = k.getActiveChatSessionId();
      // ONE CONVERSATION, ONE CAMPAIGN. If the conversation the user is in already belongs to a
      // DIFFERENT campaign (they launched a new run from an older campaign's chat), binding it
      // here would steal that campaign's conversation and leave it blank. Let the new campaign
      // resolve its own instead.
      const activeSubjectNow = activeSessionId
        ? k.getInventory().ChatSession.registry[activeSessionId]?.activeSubject
        : null;
      const alreadyOwnedByAnother =
        isEntityFocusSubject(activeSubjectNow) &&
        typeof activeSubjectNow.campaignId === 'string' &&
        activeSubjectNow.campaignId.length > 0 &&
        activeSubjectNow.campaignId !== campaign.id;
      if (activeSessionId && alreadyOwnedByAnother) {
        const own = await fetchCampaignConversation(campaign.id);
        if (own.sessionId) await k.activateChatSession(own.sessionId);
      } else if (activeSessionId) {
        // `campaignId` is the binding: from this moment the conversation BELONGS to this campaign.
        // Opening the campaign later re-activates this session; leaving it takes the chat along.
        k.setActiveSubject(activeSessionId, {
          entityType: 'Property',
          entityId: subjectPropertyId,
          focusActionType: 'ORCHESTRATE_SOURCING',
          campaignId: campaign.id,
        });
      } else {
        // No session to keep (headless, or a launch from outside a conversation): fall back to
        // the old behaviour rather than leaving the user on nothing.
        await k.focusEntityUI('Property', subjectPropertyId, 'ORCHESTRATE_SOURCING');
      }

      return campaign;
    } catch (error: any) {
      console.error('[useScoringCampaigns] launchSourcingCampaign failed', error);
      toast.error(error.message || 'Failed to launch campaign');
      throw error;
    }
  }, [kernel, readKernelDraftCampaignBudget]);

  const stopSourcingCampaign = useCallback(async (campaignId: string) => {
    const k = kernel.current;
    if (!k) return;

    // Read raw inventory snapshot for rollback safety.
    // Using getInventory() here could include merged entityKernelState drafts.
    const snapshot = k.getState().inventory;
    const previousCampaign = snapshot.SourcingCampaign.registry[campaignId] ?? null;
    const previousLeadSnapshot = Object.values(snapshot.Lead.registry).filter(
      (lead) => lead?.lastCampaignId === campaignId,
    );

    const stoppedAtIso = new Date().toISOString();
    const optimisticTimestamp = Date.now();

    if (previousCampaign) {
      k.setInventorySlice('SourcingCampaign', (prev) => {
        const currentCampaign = prev.registry[campaignId];
        if (!currentCampaign) return prev;
        return {
          ...prev,
          lastActivity: optimisticTimestamp,
          registry: {
            ...prev.registry,
            [campaignId]: {
              ...currentCampaign,
              status: 'ABORTED',
              updatedAt: stoppedAtIso,
              metrics: buildOptimisticStopMetrics(currentCampaign.metrics, stoppedAtIso),
            },
          },
        };
      });
    }

    if (previousLeadSnapshot.length > 0) {
      const previousLeadById = new Map(previousLeadSnapshot.map((lead) => [lead.id, lead]));
      k.setInventorySlice('Lead', (prev) => {
        const nextRegistry = { ...prev.registry };
        let changed = false;

        for (const lead of previousLeadSnapshot) {
          const currentLead = nextRegistry[lead.id];
          if (!currentLead) continue;

          const normalizedStatus = String(currentLead.status || '').toUpperCase();
          let nextStatus: string | null = null;
          if (normalizedStatus === 'RUNNING') {
            nextStatus = 'ABORTED';
          } else if (normalizedStatus === 'PENDING') {
            // Exactement le prédicat du serveur, `fitScore IS NOT NULL`, et rien d'autre : voir
            // settleInFlightLeadsForCampaign dans lib/leasing/campaign-lead-reconciliation.ts, la
            // seule autorité sur cette politique. L'optimiste élargi qui vivait ici divergeait des
            // deux côtés — un score de 0 partait en ABORTED là où la base écrit REVERTED, et une
            // rationale déjà streamée sans score partait en REVERTED là où la base écrit ABORTED.
            nextStatus = typeof currentLead.fitScore === 'number' ? 'REVERTED' : 'ABORTED';
          }

          if (!nextStatus || nextStatus === currentLead.status) continue;
          changed = true;
          nextRegistry[lead.id] = {
            ...currentLead,
            status: nextStatus,
            updatedAt: stoppedAtIso,
          };
        }

        if (!changed) return prev;
        return {
          ...prev,
          lastActivity: optimisticTimestamp,
          registry: nextRegistry,
        };
      });

      const rollbackLeads = () => {
        k.setInventorySlice('Lead', (prev) => {
          const nextRegistry = { ...prev.registry };
          let changed = false;
          previousLeadById.forEach((lead, leadId) => {
            if (!nextRegistry[leadId]) return;
            nextRegistry[leadId] = lead;
            changed = true;
          });
          if (!changed) return prev;
          return {
            ...prev,
            lastActivity: Date.now(),
            registry: nextRegistry,
          };
        });
      };

      try {
        const response = await fetch(`/api/inventory/sourcing-campaigns/${encodeURIComponent(campaignId)}/stop`, {
          method: 'POST',
        });

        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || 'Failed to stop campaign');
        }

        await k.syncEntity('Campaign', campaignId);
        return;
      } catch (error: any) {
        k.handleError(`The run could not be stopped: ${error?.message ?? error}`);
        rollbackLeads();
        if (previousCampaign) {
          k.setInventorySlice('SourcingCampaign', (prev) => {
            if (!prev.registry[campaignId]) return prev;
            return {
              ...prev,
              lastActivity: Date.now(),
              registry: {
                ...prev.registry,
                [campaignId]: previousCampaign,
              },
            };
          });
        }
      }
      return;
    }

    try {
      const response = await fetch(`/api/inventory/sourcing-campaigns/${encodeURIComponent(campaignId)}/stop`, {
        method: 'POST',
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to stop campaign');
      }

      await k.syncEntity('Campaign', campaignId);
    } catch (error: any) {
      k.handleError(`The run could not be stopped: ${error?.message ?? error}`);
      if (previousCampaign) {
        k.setInventorySlice('SourcingCampaign', (prev) => {
          if (!prev.registry[campaignId]) return prev;
          return {
            ...prev,
            lastActivity: Date.now(),
            registry: {
              ...prev.registry,
              [campaignId]: previousCampaign,
            },
          };
        });
      }
    }
  }, [kernel]);

  /**
   * LAUNCH A PORTFOLIO-WIDE SOURCING PASS, the thing the right bar's Launch button does at
   * portfolio scope.
   *
   * It had no kernel entrypoint at all: the panel POSTed the route inline, so the ONE surface
   * that could start a portfolio pass was that button. An agent asked to "run a portfolio-wide
   * sourcing on Altarea with 80 credits" could describe it and nothing more.
   *
   * The server reads the countries, the minimum network size and the rubric off the Portfolio
   * record, so those are saved BEFORE the launch (same order as the panel); the budget and the two
   * candidate-pool switches ride the launch payload because that is where the route reads them.
   */

  /**
   * SAVE THE COUNTRY RESTRICTION OF A PORTFOLIO'S SOURCING, the country flags of the right bar.
   *
   * The engine reads portfolioFitCountryCodes off the Portfolio record, so this write IS the
   * restriction: it is not a display preference. It only existed inline in the flag row, and two
   * other copies of the same UPDATE are STILL out there, untouched by this method: the launch brief
   * below (which writes the codes un-normalized) and the agent's sourcing commands. They are what
   * lets a normalization fixed in one surface never reach the others; this is the convergence point
   * they are meant to call, not a divergence that has already been resolved.
   *
   * Never throws and owns its localized failure toast: it answers whether the value is stored, so a
   * caller has nothing left to do but roll back its own optimistic state on false.
   */
  const savePortfolioFitCountryCodes = useCallback(async (
    portfolioId: string,
    nextCodes: string[],
  ): Promise<boolean> => {
    const k = kernel.current;
    if (!k || !portfolioId?.trim()) return false;
    const normalizedCodes = normalizePortfolioFitCountryCodes(nextCodes);
    const currentCodes = normalizePortfolioFitCountryCodes(
      k.getState().inventory.Portfolio.registry[portfolioId]?.portfolioFitCountryCodes,
    );
    // Same flags in the same order: nothing to write, and the caller must still treat it as stored.
    if (
      normalizedCodes.length === currentCodes.length &&
      normalizedCodes.every((code, index) => code === currentCodes[index])
    ) {
      return true;
    }

    try {
      await k.mutateInventory(
        [
          {
            action: 'UPDATE',
            entity: 'Portfolio',
            id: portfolioId,
            payload: { portfolioFitCountryCodes: normalizedCodes },
          },
        ],
        'Update portfolio fit country filters',
      );
      return true;
    } catch (error) {
      console.error('[useScoringCampaigns] Failed to save portfolio fit country filters', error);
      const lang = k.getState().language;
      toast.error(lang === 'fr' ? 'Echec de sauvegarde des filtres pays.' : 'Failed to save country filters.');
      return false;
    }
  }, [kernel]);

  /**
   * SAVE THE MINIMUM NETWORK SIZE OF A PORTFOLIO'S SOURCING, the other half of the candidate pool.
   *
   * Same story as the country codes above: the engine reads it off the Portfolio record to decide
   * which brands are even eligible, and the only surface that could set it was one number input.
   *
   * Never throws, owns its localized failure toast, and answers whether the value is stored.
   */
  const savePortfolioFitMinimumNetworkSize = useCallback(async (
    portfolioId: string,
    value: number,
  ): Promise<boolean> => {
    const k = kernel.current;
    if (!k || !portfolioId?.trim()) return false;
    const next = Math.max(1, Math.floor(value));
    const rawCurrent = k.getState().inventory.Portfolio.registry[portfolioId]?.portfolioFitMinimumNetworkSize;
    const current = typeof rawCurrent === 'number' && Number.isFinite(rawCurrent)
      ? Math.max(1, Math.floor(rawCurrent))
      : 1;
    if (next === current) return true;

    try {
      await k.mutateInventory(
        [
          {
            action: 'UPDATE',
            entity: 'Portfolio',
            id: portfolioId,
            payload: { portfolioFitMinimumNetworkSize: next },
          },
        ],
        'Update portfolio fit minimum network size',
      );
      return true;
    } catch (error) {
      console.error('[useScoringCampaigns] Failed to save portfolio fit minimum network size', error);
      const lang = k.getState().language;
      toast.error(lang === 'fr' ? 'Echec de sauvegarde de la taille de reseau minimum.' : 'Failed to save minimum network size.');
      return false;
    }
  }, [kernel]);

  /**
   * LAUNCH A CAMPAIGN FROM A COMPLETE SETUP BRIEF, the whole sequence behind the right bar's Launch
   * button: clean the grid, persist the brief onto the entities the engine reads it from, start the
   * run, hand the new campaign its doctrine, remember the assets and refresh the dashboard.
   *
   * The POST, the entity sync and the "open the new campaign, stay in this conversation" landing
   * already lived here (launchPortfolioFitCampaign / launchSourcingCampaign): the button used to
   * carry its own copy of both, and the copies drifted - the conversation fix landed in the kernel
   * and never reached the button, so a launch discarded the whole setup exchange. Everything else
   * the button was doing follows the same road, so a shortcut or an agent can launch the identical
   * run from a brief instead of re-inventing the order of these steps.
   *
   * Reports its own outcome and never throws: the caller is left with its own busy flag only.
   */
  const launchSourceCampaignFromBrief = useCallback(async (brief: SourceCampaignBrief) => {
    const k = kernel.current;
    if (!k || !brief.portfolioId) return;
    const lang = brief.lang;
    // The budget is a credit ceiling for the whole run, research included; the server sizes the
    // brand list to what it buys (lib/leasing/sourceBudget.ts brandsForCredits).
    const budget = Math.max(1, Math.floor(brief.budgetCredits));
    const assetIds = brief.assetIds;

    try {
      // A SOURCING TARGETS ASSETS. FULL STOP.
      //
      // An empty selection used to mean "the whole portfolio", a parallel engine writing into the
      // portfolio's SHARED reference shortlist. It was retired on 2026-08-16 along with the option
      // that produced it, because it carried every portfolio-only defect at once: a brand new
      // campaign opening onto 10,254 accumulated rows with old scores, campaigns that could not be
      // deleted (the delete route answers 404 for anything not asset-level), and campaigns launching
      // with no conversation, leaving an empty chat beside a running pass. Unit sourcing went the
      // same way on 2026-08-02, for the same reason: one product, one path.
      //
      // Refused here as well as in the panel, deliberately: the panel is one caller, and a guard
      // that lives only in the caller is a guard that the next caller does not have.
      if (assetIds.length === 0) {
        toast.error(
          lang === 'fr'
            ? 'Choisissez au moins un actif : un sourcing porte toujours sur des actifs.'
            : 'Choose at least one asset: a sourcing always targets assets.',
        );
        return;
      }

      // ONE CAMPAIGN, HOWEVER MANY ASSETS. This used to POST once per asset, so picking three
      // assets produced three campaigns, three dashboard cards and three chats from one button,
      // and the user only ever saw the first. The route now takes the whole selection and writes
      // a single campaign whose coverage is every asset picked; the engine still analyses them
      // one asset at a time, because a lead is (asset, brand) and the catchment, the tenant mix
      // and the rubric all belong to the asset, but that is now N passes of ONE run.
      await k.mutateInventory(
        buildAssetBriefUpdates(brief),
        'Update asset sourcing parameters before launch',
      );
      // Launches, syncs, and lands the user IN the new campaign's live view without leaving the
      // conversation the setup happened in (see launchSourcingCampaign above). The run fills in
      // progressively (only fully scored + vetted brands are revealed) and the user can leave
      // any time, the header up-arrow returns to the dashboard.
      //
      // Swallowed rather than rethrown: that path reports its own failure, and the catch below
      // would stack a second toast on the same one.
      const campaign = await launchSourcingCampaign(assetIds, {
        payload: {
          // A NUMBER OF BRANDS per asset, priced by the route (2026-09-27); the credit budget
          // rides along for older readers of the payload.
          brandCount: Math.max(1, Math.floor(brief.brandCount)),
          maxBudgetCredits: budget,
          // The agent speaks when the run LANDS. Only the agent's own launch proposal used
          // to set this, so a campaign started from THIS button, the one people actually
          // press, finished in silence: nothing at launch (the canned line was rightly
          // deleted) and nothing at completion either.
          recallCommanderOnComplete: true,
          useLegacyData: brief.useLegacyData !== false,
          ...(brief.countryCodes[0] ? { countryFilter: brief.countryCodes[0] } : {}),
        },
      }).catch(() => null);
      if (!campaign) return;
      // Pressing Launch is the USER's act, so the chain the setup panel's reactions built up ends
      // here. Left at depth 2, it made the landing turn refuse itself ("started by the app, already
      // 2 turns deep"): no verdict after a panel launch, and no research pass in research mode
      // (preview, 2026-09-26).
      const launchSessionId = k.getActiveChatSessionId();
      if (launchSessionId) k.patchEntityRuntimeState('ChatSession', launchSessionId, { unpromptedTurnDepth: 0 });
      // The instructions drafted in the doctrine workspace become the new shortlist's own
      // doctrine document (scope CAMPAIGN, agent SOURCE), which is what the Strategist and
      // the Judge read. Before the campaign exists there is no id to key it on, so this is
      // the first moment the draft can stop being a draft. A failure here must not fail the
      // launch: the campaign is already running and the instructions stay editable from the
      // right bar.
      await fetch('/api/doctrine', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: `campaign:${campaign.id}`, content: brief.instructions }),
      }).catch(() => undefined);
      for (const assetId of assetIds) rememberRecentAsset(kernel.current, assetId);
      // The campaign itself is already open (the kernel launch selects it); the dashboard behind
      // it still needs its re-query for the return trip.
      k.setState({
        sourceCampaignsRefreshToken: (k.getState().sourceCampaignsRefreshToken ?? 0) + 1,
      });
      // What the SERVER covered, not what was picked: an asset whose filters match nothing gets no
      // lead and is left out of the run, so announcing the selection would overstate it.
      const coveredAssetCount = Array.isArray(campaign.config?.propertyIds)
        ? campaign.config.propertyIds.length
        : 1;
      toast.success(
        coveredAssetCount > 1
          ? lang === 'fr'
            ? `Sourcing lancé sur ${coveredAssetCount} actifs, les marques apparaissent une fois vérifiées.`
            : `Sourcing launched across ${coveredAssetCount} assets, brands appear as they clear review.`
          : lang === 'fr'
            ? 'Sourcing lancé, les marques apparaissent une fois vérifiées.'
            : 'Sourcing launched, brands appear as they clear review.',
      );
    } catch (error: any) {
      console.error('[useScoringCampaigns] launchSourceCampaignFromBrief failed', error);
      toast.error(error?.message || (lang === 'fr' ? 'Échec du lancement.' : 'Failed to launch campaign.'));
    }
  }, [kernel, launchSourcingCampaign]);


  /**
   * Rename a campaign, whatever its granularity.
   *
   * ONE implementation because there were two, in SourcingCenterView and CampaignDashboard, and
   * they had already drifted on the only decision that varies: one picked the endpoint family
   * from the view's derived scope, the other from the row's targetKind, which resolve to
   * OPPOSITE families when targetKind is missing. It also belongs here rather than in a
   * component: AGENTS.md 4.1 forbids components fetching /api/* for database data, and every
   * neighbouring verb (launch, stop, destroy, reapply) already lives in this module.
   *
   * The new name is applied LOCALLY first, through the path a socket update takes, so the screen
   * is correct before any network call rather than racing a cached list query that can restore
   * the stale row - the failure that made renaming look broken twice.
   */
  const renameCampaign = useCallback(async (campaignId: string, name: string): Promise<void> => {
    const k = kernel.current;
    const next = name.trim();
    if (!k || !campaignId || !next) return;
    const row = k.getInventory().Campaign?.registry?.[campaignId] as { targetKind?: string; config?: Record<string, unknown> } | undefined;
    const isAsset = row?.targetKind === 'ASSET';
    const family = isAsset ? 'sourcing-campaigns' : 'portfolio-fit-campaigns';
    try {
      const response = await fetch(`/api/inventory/${family}/${encodeURIComponent(campaignId)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: next }),
      });
      if (!response.ok) {
        const detail = await response.json().catch(() => ({}));
        throw new Error((detail as { message?: string }).message || 'Rename failed');
      }
      k.applyInventoryEvents?.([
        {
          entityType: 'Campaign',
          entityId: campaignId,
          eventType: 'UPDATE',
          payload: { config: { ...(row?.config ?? {}), name: next } },
        } as never,
      ]);
      // ONE reconciliation, not two. The family row is already pushed to this client by the
      // route's own realtime emit, and the list re-query below refreshes the unified row anyway;
      // a second forced detail sync only bought a duplicate timeline fetch.
      await k.syncEntity('Campaign', campaignId, { force: true });
      k.setState({ sourceCampaignsRefreshToken: (k.getState()?.sourceCampaignsRefreshToken ?? 0) + 1 });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Rename failed');
    }
  }, [kernel]);

  const destroySourcingCampaignWithLeads = useCallback(async (campaignId: string) => {
    const k = kernel.current;
    if (!k || !campaignId?.trim()) {
      throw new Error('Campaign ID is required.');
    }

    const normalizedCampaignId = campaignId.trim();

    try {
      const response = await fetch(`/api/inventory/sourcing-campaigns/${encodeURIComponent(normalizedCampaignId)}`, {
        method: 'DELETE',
      });

      const payload = await response.json().catch(() => ({} as any));
      if (!response.ok) {
        throw new Error(payload?.message || 'Failed to destroy sourcing campaign.');
      }

      const localLeadIds = Object.values(k.getInventory().Lead.registry)
        .filter((lead) => lead?.lastCampaignId === normalizedCampaignId)
        .map((lead) => lead.id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0);

      const events: RealtimeEvent[] = [
        {
          entityType: 'SourcingCampaign',
          eventType: 'DELETE',
          entityId: normalizedCampaignId,
          payload: { id: normalizedCampaignId },
        },
        ...localLeadIds.map((leadId) => ({
          entityType: 'Lead' as const,
          eventType: 'DELETE' as const,
          entityId: leadId,
          payload: { id: leadId },
        })),
      ];
      k.applyInventoryEvents(events);

      const activeSessionId = k.getActiveChatSessionId();
      if (activeSessionId) {
        const activeSubject = k.getInventory().ChatSession.registry[activeSessionId]?.activeSubject ?? null;
        if (activeSubject?.entityType === 'SourcingCampaign' && activeSubject.entityId === normalizedCampaignId) {
          k.setActiveSubject(activeSessionId, null);
        }
      }

      const deletedLeadCount =
        typeof payload?.deletedLeadCount === 'number' && Number.isFinite(payload.deletedLeadCount)
          ? payload.deletedLeadCount
          : localLeadIds.length;
      toast.success(`Campaign destroyed (${deletedLeadCount} lead${deletedLeadCount > 1 ? 's' : ''} removed).`);
      return { success: true as const, deletedLeadCount };
    } catch (error: any) {
      console.error('[useScoringCampaigns] destroySourcingCampaignWithLeads failed', error);
      toast.error(error?.message || 'Failed to destroy campaign.');
      throw error;
    }
  }, [kernel]);

  // Same mould as destroySourcingCampaignWithLeads above, for the portfolio-fit family, with one
  // deliberate difference: this one is silent. The renderer's destroy toasts are localized
  // (t.destroySuccess / t.destroyFailed) and the kernel has no language, so it returns the count
  // and rethrows, and the caller keeps owning the two messages it already showed.
  const destroyPortfolioFitCampaignWithFits = useCallback(async (campaignId: string) => {
    const k = kernel.current;
    if (!k || !campaignId?.trim()) {
      throw new Error('Campaign ID is required.');
    }

    const normalizedCampaignId = campaignId.trim();

    const response = await fetch(`/api/inventory/portfolio-fit-campaigns/${encodeURIComponent(normalizedCampaignId)}`, {
      method: 'DELETE',
    });

    const payload = await response.json().catch(() => ({} as any));
    if (!response.ok) {
      // Message-less on purpose when the server says nothing: the caller supplies the localized
      // fallback, exactly as it did when this block lived in the component.
      throw new Error(typeof payload?.message === 'string' ? payload.message : '');
    }

    const localFitIds = Object.values(k.getInventory().PortfolioFit.registry)
      .filter((fit) => fit?.campaignId === normalizedCampaignId)
      .map((fit) => fit.id)
      .filter((id): id is string => typeof id === 'string' && id.length > 0);

    const events: RealtimeEvent[] = [
      {
        entityType: 'PortfolioFitCampaign',
        eventType: 'DELETE',
        entityId: normalizedCampaignId,
        payload: { id: normalizedCampaignId },
      },
      ...localFitIds.map((portfolioFitId) => ({
        entityType: 'PortfolioFit' as const,
        eventType: 'DELETE' as const,
        entityId: portfolioFitId,
        payload: { id: portfolioFitId },
      })),
    ];
    k.applyInventoryEvents(events);

    const activeSessionId = k.getActiveChatSessionId();
    if (activeSessionId) {
      const activeSubject = k.getInventory().ChatSession.registry[activeSessionId]?.activeSubject ?? null;
      if (activeSubject?.entityType === 'PortfolioFitCampaign' && activeSubject.entityId === normalizedCampaignId) {
        k.setActiveSubject(activeSessionId, null);
      }
    }

    const deletedPortfolioFitCount =
      typeof payload?.deletedPortfolioFitCount === 'number' && Number.isFinite(payload.deletedPortfolioFitCount)
        ? payload.deletedPortfolioFitCount
        : localFitIds.length;
    return { success: true as const, deletedPortfolioFitCount };
  }, [kernel]);

  const renameSourcingCampaign = useCallback(async (campaignId: string, name: string) => {
    const k = kernel.current;
    if (!k) return;

    const response = await fetch(`/api/inventory/sourcing-campaigns/${encodeURIComponent(campaignId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error((err as { message?: string }).message || 'Rename failed');
    }
    // `force`, deliberately: syncEntity returns the cached row when the entity already has
    // full detail coverage, which every campaign opened this session has. Without it the PATCH
    // succeeds, the menu closes, and the card keeps the old name - visibly "nothing happened",
    // and only for the campaigns the user has actually been working in.
    await k.syncEntity('Campaign', campaignId, { force: true });
    // The cards read the unified Campaign list, which has no detail endpoint to sync: re-query
    // it, otherwise the PATCH lands, the menu closes, and the card keeps the old name.
    k.setState({ sourceCampaignsRefreshToken: (k.getState()?.sourceCampaignsRefreshToken ?? 0) + 1 });
  }, [kernel]);

  const flushEmptyCampaigns = useCallback(async (portfolioId: string) => {
    // Auto-flush empty campaigns (terminal + zero results), so test/aborted/0-brand runs never
    // accumulate on the dashboard. Failures are non-fatal: this only tidies a list.
    await fetch('/api/inventory/campaigns/flush-empty', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ portfolioId }),
    }).catch(() => undefined);
  }, []);

  const beginSourcingCampaignDraft = useCallback(async (portfolioId?: string, propertyId?: string, unitId?: string): Promise<string | null> => {
    const k = kernel.current;
    if (!k) return null;

    // CARRY THE SOURCING SUBJECT ONTO THE NEW SESSION. ChatContextDashboard renders
    // SourcingCenterView from the ACTIVE SESSION's `activeSubject` (an ORCHESTRATE_SOURCING focus
    // on a Portfolio/Property/Unit), a session without one draws nothing at all. Minting a
    // subjectless session is what put the user on a blank screen with only the composer: new
    // conversation, correct, but no Source view behind it.
    const activeSessionId = k.getActiveChatSessionId();
    // A draft opened on an ASSET (the Launch bar) focuses that asset, so the agent's commands
    // resolve it without being told; a draft opened from the toolbar focuses the portfolio.
    const activeSubject = unitId
      ? { entityType: 'Unit' as const, entityId: unitId, focusActionType: 'ORCHESTRATE_SOURCING' as const }
      : propertyId
      ? { entityType: 'Property' as const, entityId: propertyId, focusActionType: 'ORCHESTRATE_SOURCING' as const }
      : portfolioId
      ? { entityType: 'Portfolio' as const, entityId: portfolioId, focusActionType: 'ORCHESTRATE_SOURCING' as const }
      : activeSessionId
      ? k.getInventory().ChatSession.registry[activeSessionId]?.activeSubject ?? null
      : null;
    const sourcingSubject =
      isEntityFocusSubject(activeSubject) &&
      activeSubject.focusActionType === 'ORCHESTRATE_SOURCING' &&
      Boolean(activeSubject.entityId)
        ? // STRIP THE CAMPAIGN BINDING. The subject is carried so the Source view still has
          // something to draw, but after a launch it also carries `campaignId`, and copying that
          // onto a FRESH draft session binds the new conversation to the PREVIOUS campaign. Two
          // sessions then claim one campaign, and opening it returns whichever is richer - which
          // is how "New campaign" opened showing the last campaign's analysis. A draft has no
          // campaign yet; it gets one at launch.
          { entityType: activeSubject.entityType, entityId: activeSubject.entityId, focusActionType: activeSubject.focusActionType }
        : null;

    // Hide the dashboard IMMEDIATELY, before the session round trip below. The draft flag cannot
    // be raised yet (see the ordering note), and until it is, "nothing selected and no draft" is
    // what the view reads as "show the dashboard" - so the user saw it flash on the way into a new
    // campaign. This says "a campaign is being prepared" from the click itself.
    k.setState({ sourcePreparingCampaign: true });

    // THE DRAFT FLAG IS RAISED AFTER THE SESSION SWAP, NOT BEFORE, AND THE ORDER IS THE WHOLE FIX.
    //
    // Raising it first opened the chat panel and the setup form while the ACTIVE session was still
    // the previous one, so the old conversation's transcript rendered, then vanished a moment later
    // when the new empty session took over. That is the reported "it loads something, then it
    // disappears, and then we have an empty chat": not a loading state, the wrong conversation
    // painted for one beat. Swapping first means the panel opens already pointed at the new
    // session, so the first thing drawn is the only thing drawn.
    //
    // A DRAFT NEVER OPENS IN SOMEONE ELSE'S CONVERSATION. It used to open anyway when the session
    // call failed, "keeping the conversation they were in", and that conversation could be the
    // portfolio's shared one: a new campaign showed a client's Kiabi thread (preview, 2026-09-26).
    // A failed creation is said, and the draft stays shut.
    let created: string | null = null;
    try {
      created = await k.createAndActivateSession({
        initialSubject: sourcingSubject,
        creationScope: sourcingSubject
          ? {
              module: 'source',
              entityType: sourcingSubject.entityType,
              entityId: sourcingSubject.entityId,
            }
          : { module: 'source', entityType: null, entityId: null },
      });
    } catch (error) {
      console.error('[useScoringCampaigns] beginSourcingCampaignDraft session failed', error);
    }
    if (!created) {
      k.setState({ sourcePreparingCampaign: false });
      k.handleError('Could not start a new campaign conversation. Please try again.');
      return null;
    }
    k.setState({ sourceCreatingCampaign: true, sourceSelectedCampaignId: null, sourcePreparingCampaign: false });
    return created;
  }, [kernel]);

  /**
   * THE LAUNCH BAR. The user says what they need, names the assets and a ceiling, presses Launch.
   * Pressing Launch is the authorisation: the mandate is recorded here, by the click, so the
   * agent's first turn can diagnose, configure and launch without asking (the word tests that
   * guard a chat launch never run: the mandate pays, lib/leasing/sourcingMandate.ts). The words
   * reach the agent as a typed turn, so everything downstream reads a person behind it.
   */
  const launchSourcingFromBrief = useCallback(async (brief: { prompt: string; propertyIds: string[]; unitId?: string | null; creditCeiling: number | null; targetScore: number; params?: SourcingPresetParams | null }) => {
    const k = kernel.current;
    if (!k) return;
    const propertyIds = [...new Set(brief.propertyIds.map((id) => id.trim()).filter(Boolean))];
    const prompt = brief.prompt.trim();
    const first = propertyIds[0];
    if (!prompt || !first) {
      k.handleError('Say what you need and pick at least one asset.');
      return;
    }
    // A unit fit is one unit of one asset; the unit rides on the mandate and on the draft's focus.
    const unitId = propertyIds.length === 1 && brief.unitId?.trim() ? brief.unitId.trim() : null;
    const made = createMandate({ goal: prompt, propertyIds, creditCeiling: brief.creditCeiling, origin: 'launch-bar', unitId, targetScore: brief.targetScore });
    if (!made.ok) {
      k.handleError(`Cannot launch: ${made.reason}.`);
      return;
    }
    // THE BAR'S PARAMETERS ARE THE RUN'S (2026-10-05: a bar set to 0-500 m² and 10 stores launched on
    // the asset's saved 5-1733 m² and no floor). The agent launches from the assets' saved filters,
    // and the form only ever showed the bar's values, so they are saved on the assets first.
    if (brief.params) {
      const p = brief.params;
      const registry = k.getInventory().Property.registry;
      await k.mutateInventory(propertyIds.map((id) => {
        const filters = { ...((registry[id]?.sourcingCategoryFilters ?? {}) as Record<string, unknown>) };
        const put = (key: string, value: unknown, keep: boolean) => { if (keep) filters[key] = value; else delete filters[key]; };
        if (typeof p.surfaceMinSqm === 'number') put('surfaceMinSqm', Math.round(p.surfaceMinSqm), p.surfaceMinSqm > 0);
        if (typeof p.surfaceMaxSqm === 'number') put('surfaceMaxSqm', Math.round(p.surfaceMaxSqm), p.surfaceMaxSqm > 0);
        if (typeof p.minimumNetworkSize === 'number') put('minimumNetworkSize', Math.round(p.minimumNetworkSize), p.minimumNetworkSize > 1);
        if (p.acceptedCnccActivities) put('acceptedCnccActivities', p.acceptedCnccActivities, p.acceptedCnccActivities.length > 0);
        put('countryCodes', p.countryCodes ?? [], (p.countryCodes ?? []).length > 0);
        if (typeof p.requireKnownWebsite === 'boolean') put('requireKnownWebsite', true, p.requireKnownWebsite);
        if (typeof p.excludeOffScope === 'boolean') put('excludeOffScope', false, !p.excludeOffScope);
        return {
          action: 'UPDATE' as const, entity: 'Property' as const, id,
          payload: {
            sourcingCategoryFilters: filters,
            ...(typeof p.excludeOnsiteTenants === 'boolean' ? { sourcingExclusionTenants: p.excludeOnsiteTenants } : {}),
            ...(typeof p.excludeRecentlyScored === 'boolean' ? { sourcingExclusionRecentLeads: p.excludeRecentlyScored } : {}),
          },
        };
      }), 'Save the launch bar parameters on the assets');
    }
    const portfolioId = k.getInventory().Property.registry[first]?.portfolioId;
    const sessionId = await beginSourcingCampaignDraft(portfolioId, first, unitId ?? undefined);
    if (!sessionId) return;
    // The bar's parameters go to the draft's form only now: handed over before the draft, the form
    // re-seeded from the asset when the draft focused it and dropped them.
    if (brief.params) k.setState({ sourcePendingParams: brief.params });
    for (const id of propertyIds) rememberRecentAsset(k, id);
    // THE BAR LAUNCHES, ON THE SERVER (owner, 2026-10-12). Launch used to hand the run to the agent,
    // in this tab: a launch the agent judged it could not make, or a laptop closed before its turn,
    // started nothing (12 Oct, 07:07: "the launch command is unavailable", no campaign). The first
    // pass and the mandate go to the route now, and the worker's driver (lib/leasing/mandateDriver.ts)
    // carries every pass after it, tab open or not. The agent then only says what it will do.
    // The launch grades the whole pool before the run exists, seconds on a busy server: the
    // conversation says so from the first frame rather than staying blank (2026-10-12, ~20 s).
    k.setSessionStatus(sessionId, 'Starting the run...');
    const firstPass = Math.min(made.mandate.creditCeiling, creditsForBrands(FIRST_PASS_BRANDS * propertyIds.length, true));
    const committed = commitToMandate(made.mandate, firstPass);
    try {
      await k.launchSourcingCampaign(propertyIds.length === 1 ? first : propertyIds, {
        payload: {
          maxBudgetCredits: firstPass,
          recallCommanderOnComplete: false,
          ...(unitId ? { unitId } : {}),
          mandate: { goal: committed.goal, targetScore: committed.targetScore ?? MANDATE_DEFAULT_TARGET_SCORE, creditCeiling: committed.creditCeiling, committedCredits: committed.committedCredits, expiresAt: committed.expiresAt, chatSessionId: sessionId },
        },
      });
    } catch {
      k.setSessionStatus(sessionId, '');
      return; // launchSourcingCampaign already told the user why
    }
    k.logUserAction?.('Sourcing launched from the brief bar', { sessionId, propertyIds, unitId, creditCeiling: made.mandate.creditCeiling, targetScore: made.mandate.targetScore, firstPass });
    // The run exists and the server refuses a second one on the same asset while it runs; the agent
    // reads it in the conversation's subject and answers the brief.
    await k.askAssistant(sessionId, prompt);
  }, [kernel, beginSourcingCampaignDraft]);

  /**
   * Open a campaign, with ITS conversation. The only correct way to open a campaign.
   *
   * The resolution is not made here. The server owns it
   * (POST /api/inventory/sourcing-campaigns/[id]/conversation): it sees every session and every
   * campaign of the asset rather than a paginated slice, decides in one transaction, and returns
   * exactly one conversation id, creating and binding one if the campaign has none. That is what
   * makes a campaign's conversation stable, exclusive, and the same on every device.
   *
   * What is left here is deliberately dumb: ask, then show. No fallbacks, no heuristics, and no
   * second opinion, because every version of this that held its own opinion produced a chat that
   * appeared and then vanished.
   */
  const openSourceCampaign = useCallback(async (campaignId: string) => {
    const k = kernel.current;
    if (!k || !campaignId) return;

    // Paint the shell now, but keep live data behind it until the conversation and its subject
    // are settled. Selecting early mounts the shortlist under the previous session's entity.
    const request = ++openCampaignRequestRef.current;
    k.setState({ sourceOpeningCampaignId: campaignId, sourceOpeningRentRollId: null });
    const stillWanted = () => openCampaignRequestRef.current === request
      && k.getState().sourceOpeningCampaignId === campaignId;

    try {
    // ALWAYS ASK. An earlier version gated this on the campaign being in the local registry, to
    // avoid a 404 for portfolio-fit and unit-fit cards that share this handler. That registry is a
    // cache: on a cold dashboard the campaign is not in it yet, the resolution was skipped, and
    // the user was left looking at the previous conversation. That is the "wrong chat" bug, and it
    // even made a test pass by leaving a populated conversation on screen. The 404 is
    // distinguished by the server's answer instead, which is the only thing that knows.
    const resolved = await fetchCampaignConversation(campaignId);
    // A newer open (or a close) started while we were asking: that one owns the screen now.
    if (!stillWanted()) return;

    if (resolved.notFound) {
      // Another campaign family. It owns no conversation, so there is nothing to activate and
      // nothing to wait for: open it against the conversation already on screen, quietly.
      k.setState({ sourceSelectedCampaignId: campaignId, sourceCreatingCampaign: false });
      return;
    }
    if (!resolved.sessionId) {
      // An unanswered question is not an answer of "none": say so rather than swapping the user
      // onto nothing, and leave them where they are.
      throw new Error('Could not open this campaign. Please try again.');
    }

    // THE SUBJECT IS SETTLED BEFORE THE TRANSITION, NOT DURING IT.
    //
    // A conversation this resolve just CREATED has no subject, and the whole Source surface is
    // derived from one - so without this the click lands on an empty screen. But doing it after
    // the activation put two awaits and a subject write INSIDE the window this function exists to
    // keep shut: resolve and activate, then select, once. Everything the user has complained about
    // on this screen (re-ranks a second later, chat arrives late, bounces back to the dashboard,
    // loads then reloads) is that window being open. So the subject is prepared here, on a session
    // that is not on screen yet, and the transition below stays a single step.
    const sessionId = resolved.sessionId;
    const sessionRow = k.getInventory().ChatSession.registry[sessionId];
    // MISSING AND STALE ARE THE SAME PROBLEM. A conversation belonging to THIS campaign must be
    // focused on THIS campaign's entity. Only checking for a missing subject fixed the new-
    // conversation case and left the worse one: opening a portfolio pass straight after an asset
    // shortlist kept the asset's subject, so the header still read "Asset · Aubergenville" over an
    // empty table - the campaign was open, on the wrong entity, showing nothing.
    const existingSubject = sessionRow?.activeSubject;
    const subjectMatchesCampaign =
      isEntityFocusSubject(existingSubject) && existingSubject.campaignId === campaignId;
    if (!subjectMatchesCampaign) {
      // On a cold dashboard neither campaign row is loaded, and a subject cannot be derived from a
      // row we do not hold. Ask once - still before the transition.
      // ONE ROW, ONE REGISTRY. This block asked two family registries which kind of campaign it
      // had, and one of them - PortfolioFitCampaign - has held zero rows since the tables were
      // unified, so half the dispatch was answering "no" by construction. The unified row carries
      // its own granularity and both of its parents, so it answers on its own.
      if (!k.getInventory().Campaign.registry[campaignId]) {
        await k.syncEntity('Campaign', campaignId);
        if (!stillWanted()) return;
      }
      const row = k.getInventory().Campaign.registry[campaignId] as
        | { propertyIds?: string[] | null; portfolioId?: string | null }
        | undefined;
      const propertyId = Array.isArray(row?.propertyIds) && typeof row.propertyIds[0] === 'string' ? row.propertyIds[0] : '';
      const portfolioId = typeof row?.portfolioId === 'string' ? row.portfolioId : '';
      if (propertyId) {
        k.setActiveSubject(sessionId, {
          entityType: 'Property',
          entityId: propertyId,
          focusActionType: 'ORCHESTRATE_SOURCING',
          campaignId,
        });
      } else if (portfolioId) {
        k.setActiveSubject(sessionId, {
          entityType: 'Portfolio',
          entityId: portfolioId,
          focusActionType: 'ORCHESTRATE_SOURCING',
          campaignId,
        });
      }
    }

    if (k.getActiveChatSessionId() !== resolved.sessionId) {
      // Say WHICH campaign this activation belongs to. The reconciliation on the other side reads
      // the binding off the local ChatSession row, and a conversation this resolve just created
      // reads as unbound there - which used to close the campaign the user had just opened.
      await k.activateChatSession(resolved.sessionId, { openingCampaignId: campaignId, isStillWanted: stillWanted });
      if (!stillWanted()) return;
      if (k.getActiveChatSessionId() !== resolved.sessionId) {
        throw new Error('Could not load this campaign conversation. Please try again.');
      }
    }

    // Activating a BOUND conversation has already selected this campaign (the reverse half of the
    // binding, in useChat). Writing the same value again is a second store notification for no
    // change, which is the sort of extra render this whole reordering exists to remove.
    const state = k.getState();
    if (state.sourceSelectedCampaignId !== campaignId || state.sourceCreatingCampaign) {
      k.setState({ sourceSelectedCampaignId: campaignId, sourceCreatingCampaign: false });
    }
    } catch (error) {
      if (stillWanted()) k.handleError(error instanceof Error ? error.message : String(error));
    } finally {
      if (stillWanted()) k.setState({ sourceOpeningCampaignId: null });
    }
  }, [kernel]);

  /**
   * Destroy a campaign opened from the Source center and clear the view state that
   * pointed at it: the open campaign falls back to the dashboard, and the refresh
   * token re-queries so counts and pagination settle. The underlying destroy already
   * reports its own failure, so callers only need to handle the throw.
   */
  /**
   * Destroy a campaign of ANY granularity, then do the aftermath once.
   *
   * The first argument used to be typed `'SourcingCampaign'` and then ignored: the body always
   * called the asset destroyer. So the one call site looked like it was choosing a family while
   * choosing nothing, and deleting a portfolio campaign from the dashboard hit the asset route,
   * which filters on `targetKind: ASSET` and answered 404 "Sourcing campaign not found". Driven in
   * a browser on 2026-08-15: 23 of 125 campaigns (14 portfolio, 9 unit) could not be deleted at
   * all, the error toast appeared twice, and the card was still there after a reload.
   *
   * The portfolio destroyer already existed and was never called from that screen. Dispatching here
   * rather than at the call site keeps the aftermath - clearing the selection, handing the chat back
   * to the portfolio conversation - shared, which is the reason this function exists at all.
   */
  const destroySourceCampaign = useCallback(async (
    entityType: CampaignTargetKind | 'SourcingCampaign',
    campaignId: string,
  ): Promise<void> => {
    const k = kernel.current;
    if (!k) throw new Error('Kernel not available');

    if (entityType === 'PORTFOLIO') {
      await destroyPortfolioFitCampaignWithFits(campaignId);
    } else if (String(entityType) === 'UNIT') {
      // Compared as a string ON PURPOSE, and the compiler is right to find it odd: CampaignTargetKind
      // is 'PORTFOLIO' | 'ASSET', so this branch is unreachable through the type. The DATABASE
      // disagrees - 9 rows carry targetKind 'UNIT' - because unit sourcing was removed on 2026-08-02
      // and its rows outlived the type that described them. Without this branch such a row falls to
      // the asset destroyer, which filters on targetKind and answers 404, and the user re-clicks a
      // button that cannot work. Say what is true instead.
      throw new Error('Unit campaigns cannot be deleted: unit sourcing was removed.');
    } else {
      await destroySourcingCampaignWithLeads(campaignId);
    }

    if (k.getState().sourceSelectedCampaignId === campaignId) {
      k.setState({ sourceSelectedCampaignId: null, sourceFocusedFitId: null, sourceSelectedFitIds: [] });
      // Destroying the OPEN campaign closes it like the back button does: the chat is handed to
      // the dashboard conversation rather than the dead campaign's conversation staying up over
      // the dashboard. Its session keeps a binding to the dead id, which is harmless - opening
      // resolves against the live registries and self-heals to the dashboard.
      const activeSessionId = k.getActiveChatSessionId();
      const activeSubject = activeSessionId
        ? k.getInventory().ChatSession.registry[activeSessionId]?.activeSubject
        : null;
      const boundHere =
        isEntityFocusSubject(activeSubject) && activeSubject.campaignId === campaignId;
      if (boundHere) {
        const propertyId =
          typeof activeSubject.entityId === 'string' ? activeSubject.entityId : '';
        const portfolioId = propertyId
          ? ((k.getInventory().Property.registry[propertyId] as { portfolioId?: string } | undefined)
              ?.portfolioId ?? '')
          : '';
        if (portfolioId) {
          void k.focusEntityUI('Portfolio', portfolioId, 'ORCHESTRATE_SOURCING');
        }
      }
    }
    k.setState({ sourceCampaignsRefreshToken: k.getState().sourceCampaignsRefreshToken + 1 });
  }, [kernel, destroySourcingCampaignWithLeads, destroyPortfolioFitCampaignWithFits]);

  /**
   * Close the open campaign and hand the chat back to the portfolio's dashboard conversation.
   * The reverse half of openSourceCampaign: a campaign never opens without its own conversation,
   * and never closes while keeping it. Clearing the selected id alone is what used to leave
   * campaign A's shortlist on screen next to campaign B's chat.
   */
  const closeSourceCampaign = useCallback(() => {
    const k = kernel.current;
    if (!k) return;
    openCampaignRequestRef.current += 1;
    k.setState({ sourceOpeningCampaignId: null });

    // Resolve the dashboard's portfolio fresh, at call time, from the campaign being closed:
    // campaign -> property -> portfolio. Read through the kernel, not a caller's closure, so the
    // value can never be stale.
    const closingId = k.getState().sourceSelectedCampaignId;
    const inv = k.getInventory();
    const closingCampaign = closingId ? inv.SourcingCampaign.registry[closingId] : undefined;
    const closingPropertyId =
      typeof closingCampaign?.propertyId === 'string' ? closingCampaign.propertyId : '';
    // Fall back to the org's default Source portfolio rather than silently not handing the chat
    // back: a close that leaves the campaign's conversation talking over the dashboard is the bug
    // this whole path exists to prevent.
    const portfolioId =
      (closingPropertyId
        ? ((inv.Property.registry[closingPropertyId] as { portfolioId?: string } | undefined)
            ?.portfolioId ?? '')
        : '') ||
      (resolveDefaultSourcePortfolioId(k.getUserContext?.()?.organisationId ?? null) ?? '');
    k.setState({ sourceSelectedCampaignId: null });
    if (portfolioId) {
      void k.focusEntityUI('Portfolio', portfolioId, 'ORCHESTRATE_SOURCING');
    }
  }, [kernel]);

  const reapplySourcingCampaignRubric = useCallback(async (
    campaignId: string,
  ): Promise<ReapplySourcingRubricResult> => {
    const k = kernel.current;
    if (!k || !campaignId?.trim()) {
      throw new Error('Campaign ID is required.');
    }

    const normalizedCampaignId = campaignId.trim();
    const releaseRescoreFlag = markRescoreInFlight(kernel);

    try {
      const response = await fetch(
        `/api/inventory/sourcing-campaigns/${encodeURIComponent(normalizedCampaignId)}/reapply-rubric`,
        { method: 'POST' },
      );
      const payload = (await response.json().catch(() => ({}))) as Record<string, unknown>;
      if (!response.ok) {
        // The status is carried on the error, not just the text: 409 is the server saying the
        // stored evidence cannot be rescaled (a criterion was added or removed), which callers
        // must treat as "relaunch", never as "retry".
        const failure = new Error(
          (typeof payload.message === 'string' && payload.message) || 'Failed to reapply rubric.',
        ) as Error & { status?: number };
        failure.status = response.status;
        throw failure;
      }
      return {
        summary: (payload.summary ?? null) as ReapplySourcingRubricResult['summary'],
        rubricMismatch: (payload.rubricMismatch ?? null) as ReapplySourcingRubricResult['rubricMismatch'],
      };
    } catch (error) {
      console.error('[useScoringCampaigns] reapplySourcingCampaignRubric failed', error);
      throw error;
    } finally {
      releaseRescoreFlag();
    }
  }, [kernel]);

  /**
   * Re-run the FULL analysis (fresh research + fresh scoring) for one brand of a shortlist, or for
   * every brand of a row selection. The expensive sibling of reapplySourcingCampaignRubric: that
   * one rescales stored points for free, this one queues a real, billed agent pass on the worker,
   * one credit per brand.
   *
   * It raises the shared re-score flag like the re-apply path does, so the shortlist says it is
   * being reworked from the click onwards. The flag only has to cover the queue round trip: once
   * the campaign row comes back WAITING/RUNNING, the view keeps the sweep lit off the campaign
   * itself for as long as the pass lasts.
   */
  const rerunSourcingCampaignBrand = useCallback(async (
    campaignId: string,
    brands: string | string[],
  ): Promise<void> => {
    const k = kernel.current;
    // Deduped here as well as on the server, because the count the user was shown is the count
    // they expect to be billed for, and the panel resolves brands from rows.
    const brandIds = Array.from(
      new Set((Array.isArray(brands) ? brands : [brands]).map((id) => (typeof id === 'string' ? id.trim() : '')).filter(Boolean)),
    );
    if (!k || !campaignId?.trim() || brandIds.length === 0) {
      throw new Error('Campaign ID and at least one brand ID are required.');
    }

    const releaseRescoreFlag = markRescoreInFlight(kernel);
    try {
      const response = await fetch(
        `/api/inventory/sourcing-campaigns/${encodeURIComponent(campaignId.trim())}/rerun-brand`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ brandIds }),
        },
      );
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as Record<string, unknown>;
        const failure = new Error(
          (typeof payload.message === 'string' && payload.message) || 'Failed to queue the re-analysis.',
        ) as Error & { status?: number };
        failure.status = response.status;
        throw failure;
      }
      // The campaign's WAITING state and each lead's PENDING state are emitted by the route over
      // the usual socket path, so there is nothing to patch in locally.
    } catch (error) {
      console.error('[useScoringCampaigns] rerunSourcingCampaignBrand failed', error);
      throw error;
    } finally {
      releaseRescoreFlag();
    }
  }, [kernel]);


  const setLeadStatus = useCallback(async (leadId: string, status: 'INTERESTING' | 'DISMISSED') => {
    const k = kernel.current;
    if (!k) throw new Error('Kernel not available');
    const normalizedLeadId = leadId.trim();
    if (!normalizedLeadId) throw new Error('Lead id is required.');

    await k.mutateInventory([{
      action: 'UPDATE',
      entity: 'Lead',
      id: normalizedLeadId,
      payload: { status },
    }], `Set lead status to ${status}`);
  }, [kernel]);

  const flagLeadInteresting = useCallback(async (leadId: string) => {
    const k = kernel.current;
    if (!k) throw new Error('Kernel not available');
    const normalizedLeadId = leadId.trim();
    if (!normalizedLeadId) return;

    await setLeadStatus(normalizedLeadId, 'INTERESTING');
  }, [kernel, setLeadStatus]);

  const flagLeadDismissed = useCallback(async (leadId: string) => {
    const k = kernel.current;
    if (!k) throw new Error('Kernel not available');
    const normalizedLeadId = leadId.trim();
    if (!normalizedLeadId) return;

    await setLeadStatus(normalizedLeadId, 'DISMISSED');
  }, [kernel, setLeadStatus]);

  const setPortfolioFitStatus = useCallback(async (portfolioFitId: string, status: 'INTERESTING' | 'DISMISSED') => {
    const k = kernel.current;
    if (!k) throw new Error('Kernel not available');
    const normalizedPortfolioFitId = portfolioFitId.trim();
    if (!normalizedPortfolioFitId) throw new Error('Portfolio fit id is required.');

    await k.mutateInventory([{
      action: 'UPDATE',
      entity: 'PortfolioFit',
      id: normalizedPortfolioFitId,
      payload: { status },
    }], `Set portfolio fit status to ${status}`);
  }, [kernel]);

  const flagPortfolioFitInteresting = useCallback(async (portfolioFitId: string) => {
    const k = kernel.current;
    if (!k) throw new Error('Kernel not available');
    const normalizedPortfolioFitId = portfolioFitId.trim();
    if (!normalizedPortfolioFitId) return;

    await setPortfolioFitStatus(normalizedPortfolioFitId, 'INTERESTING');
  }, [kernel, setPortfolioFitStatus]);

  const flagPortfolioFitDismissed = useCallback(async (portfolioFitId: string) => {
    const k = kernel.current;
    if (!k) throw new Error('Kernel not available');
    const normalizedPortfolioFitId = portfolioFitId.trim();
    if (!normalizedPortfolioFitId) return;

    await setPortfolioFitStatus(normalizedPortfolioFitId, 'DISMISSED');
  }, [kernel, setPortfolioFitStatus]);

  return {
    sourceBudgetProposal,
    launchSourcingCampaign,
    stopSourcingCampaign,
    savePortfolioFitCountryCodes,
    savePortfolioFitMinimumNetworkSize,
    launchSourceCampaignFromBrief,
    destroySourcingCampaignWithLeads,
    renameCampaign,
    destroyPortfolioFitCampaignWithFits,
    renameSourcingCampaign,
    flushEmptyCampaigns,
    beginSourcingCampaignDraft,
    launchSourcingFromBrief,
    openSourceCampaign,
    destroySourceCampaign,
    closeSourceCampaign,
    reapplySourcingCampaignRubric,
    rerunSourcingCampaignBrand,
    handleCampaignEvent,
    flagLeadInteresting,
    flagLeadDismissed,
    flagPortfolioFitInteresting,
    flagPortfolioFitDismissed,
  };
};
